/**
 * Proposal email content — one builder used by the wizard preview and by
 * the "Send via M365" action so what the user sees is what is sent.
 * Customer-facing: totals only, never cost basis or margin.
 */
import { formatCurrency, formatDate, formatSolutionType } from './formatters';

export interface ProposalEmailInput {
  estimateNumber: string | null;
  contactName: string | null;
  customerName: string | null;
  locationName: string | null;
  solutionType: string | null;
  hardwareCents: number;
  laborCents: number;
  contingencyCents: number;
  contingencyPct: number;
  totalCents: number;
  /** ISO date (YYYY-MM-DD) or null when no validity window is set. */
  validUntil: string | null;
  footer: string | null;
}

export interface ProposalEmailText {
  subject: string;
  greeting: string;
  intro: string;
  amounts: Array<{ label: string; value: string }>;
  total: string;
  validity: string | null;
  closing: string;
  signature: string;
  footer: string | null;
}

export function buildProposalSubject(input: ProposalEmailInput): string {
  const solution = input.solutionType ? formatSolutionType(input.solutionType) : 'Proposal';
  const forWhom = input.customerName ? ` for ${input.customerName}` : '';
  const number = input.estimateNumber ? ` (${input.estimateNumber})` : '';
  return `PazNetworks, Inc. Proposal — ${solution}${forWhom}${number}`;
}

export function buildProposalText(input: ProposalEmailInput): ProposalEmailText {
  const solution = input.solutionType ? formatSolutionType(input.solutionType) : 'technology';
  const site = input.locationName ?? 'your site';
  return {
    subject: buildProposalSubject(input),
    greeting: `Dear ${input.contactName?.trim() || 'Customer'},`,
    intro: `Thank you for the opportunity to provide a ${solution} solution for ${site}. Based on our site assessment, we are pleased to present the following proposal:`,
    amounts: [
      { label: 'Hardware', value: formatCurrency(input.hardwareCents) },
      { label: 'Labor', value: formatCurrency(input.laborCents) },
      { label: `Contingency (${input.contingencyPct}%)`, value: formatCurrency(input.contingencyCents) },
    ],
    total: formatCurrency(input.totalCents),
    validity: input.validUntil ? `This proposal is valid until ${formatDate(input.validUntil)}.` : null,
    closing: 'Please reach out with any questions.',
    signature: 'Best regards,\nPazNetworks, Inc. Sales Team',
    footer: input.footer?.trim() || null,
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function nl2br(s: string): string {
  return escapeHtml(s).replace(/\n/g, '<br />');
}

export function buildProposalHtml(input: ProposalEmailInput): string {
  const t = buildProposalText(input);
  const rows = t.amounts
    .map(
      (a) =>
        `<tr><td style="padding:4px 12px 4px 0;color:#475569">${escapeHtml(a.label)}</td><td style="padding:4px 0;text-align:right">${escapeHtml(a.value)}</td></tr>`,
    )
    .join('');
  return [
    '<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#0f172a;line-height:1.5">',
    `<p>${escapeHtml(t.greeting)}</p>`,
    `<p>${escapeHtml(t.intro)}</p>`,
    '<table style="border-collapse:collapse;margin:12px 0">',
    rows,
    `<tr><td style="padding:8px 12px 4px 0;font-weight:bold;border-top:1px solid #cbd5e1">Total</td><td style="padding:8px 0 4px;text-align:right;font-weight:bold;border-top:1px solid #cbd5e1">${escapeHtml(t.total)}</td></tr>`,
    '</table>',
    t.validity ? `<p>${escapeHtml(t.validity)}</p>` : '',
    `<p>${escapeHtml(t.closing)}</p>`,
    `<p>${nl2br(t.signature)}</p>`,
    t.footer ? `<p style="font-size:12px;color:#64748b">${nl2br(t.footer)}</p>` : '',
    '</div>',
  ].join('');
}
